/**
 * Model-space extent (bounding box) computation over shell-level
 * `EntityPreview` objects. Used by `MVIEW` to auto-fit newly created
 * paper-space viewports to the current model geometry — matches
 * AutoCAD's default "FIT" / "corner" behaviour where the viewport's
 * view centre and view height are derived from the model extent.
 *
 * The shell `EntityPreview` carries positional data as a string map
 * (see `deriveXxxPreviewProperties` in `previewBuilders.ts`). Each
 * entity type stores its own keys; this module reads those keys and
 * contributes one or more (x, y) points to the running bbox.
 *
 * Types whose shell representation does NOT carry coordinates (e.g.
 * `Hatch` — pattern name and boundary COUNT, never the boundary paths)
 * are skipped. AutoCAD's EXTENTS command handles these via their cached
 * bounding box; for MVIEW-FIT we accept a conservative under-
 * approximation here, and the richer `DrawingDocument` path (which
 * holds real vertex arrays) can later replace this with a precise
 * computation without changing the public function signature.
 *
 * # Relationship to the TYPED bounds truth
 *
 * The exact "where is this entity" answer is the typed walk over
 * `DrawingEntity` — `drawing-model`'s `computeEntityWcsBounds` and
 * `cad-core`'s `getEntityBounds` / `heavyPolylineDrawnBounds`, which the
 * spatial/metadata index (`apps/web/.../entityMetadataIndex.ts
 * ::boundsForEntity`) and the viewport-membership index
 * (`drawing-model/viewportMembership.ts::entityModelBounds`) both consume.
 * Those take the OCS lift, stroked width bands, bulge extrema and — for a
 * `Table` — the anonymous `*T<N>` graphics BLOCK into account, none of
 * which a shell string bag carries. This module deliberately does NOT
 * re-derive any of that: it reads the coordinates the preview bag really
 * publishes and says so per kind. Where the bag is a strictly coarser
 * view than the typed walk, the arm below names what it loses.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { bulgedSpanHullCorners } from "./extentPolylineBulge";
import {
  parseClosedFlagProperty,
  parsePointListProperty,
} from "./geometryEntityTypes";
import { parseBulgeList } from "./pathSampling";

export type ModelExtent = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

function parseNumericProperty(
  properties: Record<string, string>,
  key: string,
): number | null {
  const raw = properties[key];
  // DOCUMENTED EQUIVALENT MUTANT (targeted mutation testing): `ConditionalExpression`
  // forcing the `raw === undefined` HALF of this guard to `false`. An absent key
  // then falls through to `Number(undefined)`, which is `NaN`, which the
  // `Number.isFinite` return below already answers as `null` — the same value,
  // so no input can discriminate the two. The half is kept because it states
  // the ABSENT case at the top instead of leaving a reader to re-derive it from
  // `Number`'s coercion table, and because it short-circuits the common case.
  // The `raw === ""` half is NOT equivalent (`Number("")` is `0`, a coordinate
  // the drawing never mentioned) and is killed by this module's own
  // "an EMPTY numeric cell is not zero" witness.
  if (raw === undefined || raw === "") return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function parsePoint(
  properties: Record<string, string>,
  xKey: string,
  yKey: string,
): { x: number; y: number } | null {
  const x = parseNumericProperty(properties, xKey);
  const y = parseNumericProperty(properties, yKey);
  if (x === null || y === null) return null;
  return { x, y };
}

/**
 * Extract candidate (x, y) points from a single `EntityPreview`. Empty
 * array signals "no bound contribution" — e.g. for Hatch, LWPOLYLINE
 * (vertex count only), and other types whose shell representation
 * omits coordinates.
 */
function extractEntityPoints(
  entity: EntityPreview,
): { x: number; y: number }[] {
  const props = entity.properties;
  const points: { x: number; y: number }[] = [];

  switch (entity.type) {
    case "Line": {
      const start = parsePoint(props, "Start X", "Start Y");
      const end = parsePoint(props, "End X", "End Y");
      if (start) points.push(start);
      if (end) points.push(end);
      return points;
    }

    case "Circle":
    case "Arc": {
      // Over-approximate with the bounding square of the full circle.
      // For arcs this is a superset of the actual arc bbox, which is
      // acceptable for MVIEW auto-fit (matches AutoCAD's generous bbox
      // used by ZOOM EXTENTS on arcs).
      const center = parsePoint(props, "Center X", "Center Y");
      const radius = parseNumericProperty(props, "Radius");
      if (center && radius !== null && radius >= 0) {
        points.push({ x: center.x - radius, y: center.y - radius });
        points.push({ x: center.x + radius, y: center.y + radius });
      }
      return points;
    }

    case "Text":
    case "MText": {
      // Zero-size insertion point approximation. Shell EntityPreview
      // does not carry glyph bounding boxes; precise text extents
      // require the font-metric path used by the renderer.
      const insertion = parsePoint(props, "Insertion X", "Insertion Y");
      if (insertion) points.push(insertion);
      return points;
    }

    case "Dimension": {
      const ext1 = parsePoint(props, "Extension 1 X", "Extension 1 Y");
      const ext2 = parsePoint(props, "Extension 2 X", "Extension 2 Y");
      const dimLine = parsePoint(props, "Dimension line X", "Dimension line Y");
      if (ext1) points.push(ext1);
      if (ext2) points.push(ext2);
      if (dimLine) points.push(dimLine);
      return points;
    }

    case "Multileader": {
      const arrow = parsePoint(props, "Arrow X", "Arrow Y");
      const landing = parsePoint(props, "Landing X", "Landing Y");
      if (arrow) points.push(arrow);
      if (landing) points.push(landing);
      return points;
    }

    case "Revision Cloud": {
      const corner1 = parsePoint(props, "Corner 1 X", "Corner 1 Y");
      const corner2 = parsePoint(props, "Corner 2 X", "Corner 2 Y");
      if (corner1) points.push(corner1);
      if (corner2) points.push(corner2);
      return points;
    }

    case "Block": {
      // Zero-size insertion point; a precise block-extent walk would
      // need to resolve the block definition and project its child
      // entities through insertionPoint/rotation/scale — out of scope
      // for the shell-level path used by MVIEW.
      const insertion = parsePoint(props, "Insertion X", "Insertion Y");
      if (insertion) points.push(insertion);
      return points;
    }

    case "Rectangular Associative Array": {
      const base = parsePoint(props, "Base X", "Base Y");
      if (base) points.push(base);
      return points;
    }

    case "LWPOLYLINE":
    case "Polyline": {
      // Both polyline families publish the SAME `x,y;x,y` vertex wire form
      // (`dxfGeometry.buildLwpolylinePreview` / `buildPolylinePreview` and
      // `previewBuilders.deriveLwpolylinePreviewProperties`), decoded through
      // the ONE parser that serialization's `formatNumber` half lives beside.
      //
      // The module header's old claim that LWPOLYLINE carries "only vertex
      // COUNT" described a builder that no longer exists: the bare-count form
      // was replaced by the coordinate list in both builders, and the heavy
      // `Polyline` admitted by this PR was written against the same
      // convention from the start.
      //
      // THE BULGE BOW IS NO LONGER LOST (@codex PR #785 round 15, FINDING 1).
      // A bulged span is a circular ARC that bows OUTSIDE its chord, so the
      // chord hull alone is DEGENERATE for the shape a two-vertex bulged
      // polyline really is: the semicircle `(0, 0) → (20, 0)` with bulge 1
      // gave `minY === maxY`, `createViewportEntity`'s `modelH > 0` gate was
      // false, and the new viewport fell back to the unrelated (0, 0) /
      // viewHeight-100 view. `./extentPolylineBulge.ts` supplies the bow — read
      // its header for WHICH SIDE of the chord it puts it on (the ARC AS DRAWN
      // since the r17 P1 fix corrected `bulgeArc`'s orientation; the
      // chord-symmetric hull that used to guard against the mirror is gone),
      // and for the 40 000-case measurement against the RENDERER's own
      // `bulgeToArc` that pins it.
      //
      // The LWPOLYLINE bag publishes the per-vertex group-42 list as `Bulges`
      // (both producers: `dxfGeometry.buildLwpolylinePreview` and
      // `previewBuilders.deriveLwpolylinePreviewProperties`). The heavy
      // `Polyline` bag does NOT — `dxfGeometry.buildPolylinePreview` emits
      // `Vertices` / `Closed` and no bulge row at all — so a heavy polyline
      // still contributes its CHORD hull here. That is a DECLARED residual
      // with a one-line completion (emit the `Bulges` sibling from
      // `buildPolylinePreview`, exactly as the LWPOLYLINE builder beside it
      // already does, and this arm needs no further change because it reads
      // the row for both types); it is not done in this change because adding
      // a bag row is a preview-model edit with its own descriptor-census and
      // Properties-palette surface.
      //
      // What this hull STILL loses against the typed truth
      // (`cad-core/heavyPolylineDrawnBounds`, which the spatial index uses):
      // the stroked WIDTH band (groups 40/41/43), the group-39 thickness sweep
      // and the group-210 OCS lift — none of those three values is in the bag
      // at all. It also counts a poly-face mesh's FACE records, whose 10/20/30
      // is filler, so a polyface far from the origin stretches the box toward
      // it. Every one of those is a WIDENING or a shift of a box that is
      // otherwise anchored on real vertices, so for MVIEW-FIT — where the
      // alternative is the no-extent fallback that parks the new viewport at
      // (0, 0) with viewHeight 100 and shows nothing at all — this frames the
      // object. A precise fit needs the typed walk, which needs a
      // `DrawingEntity`.
      //
      // An ABSENT row and an unparseable one are handled separately rather
      // than folded together through a `?? ""` default: the two differ (`""`
      // decodes to an EMPTY list, `undefined` has nothing to decode), and
      // collapsing them makes the default string unobservable from the outside
      // — a mutation-equivalent branch hiding a real distinction.
      const serialized = props["Vertices"];
      const vertices =
        serialized === undefined ? null : parsePointListProperty(serialized);
      if (!vertices) return points;
      points.push(...vertices);
      // `parseBulgeList` is `./pathSampling.ts`' ONE reading of the `Bulges`
      // row — the same call the GRIPS render and drag paths make, so the span
      // this arm bows and the span the user sees bowed cannot disagree about
      // WHICH span carries the value. It returns `[]` for an absent row, which
      // `bulgedSpanHullCorners`' count lock then rejects, leaving the chord
      // hull exactly as before for every bulge-less polyline.
      points.push(
        ...bulgedSpanHullCorners(
          vertices,
          parseBulgeList(props["Bulges"]),
          parseClosedFlagProperty(props["Closed"]),
        ),
      );
      return points;
    }

    case "Table": {
      // AcDbTable is a block reference (group 2 names the anonymous `*T<N>`
      // block that carries the drawn grid), so the EXACT footprint is that
      // block's bounds — `drawing-model/wcsBounds.ts::tableWcsBounds` over
      // `tableAsBlockReference`, which needs the document's block table. A
      // shell `EntityPreview` has no block table, so this arm reconstructs the
      // NOMINAL grid rectangle from the three rows the bag does publish:
      // the insertion point (groups 10/20), the summed column widths / row
      // heights (`Table width` / `Table height`, groups 142 / 141) and the
      // rotation derived from the group-11 horizontal-direction VECTOR.
      //
      // Anchor convention: AutoCAD places a table's UPPER-LEFT corner at the
      // insertion point and flows it right along the horizontal direction and
      // DOWNWARD (the default TABLESTYLE flow direction). Our `TableEntity`
      // does not carry a flow-direction field — the TABLESTYLE object that
      // owns it is not parsed — so the default is what this reconstructs; an
      // "up"-flow table is fitted a table-height off in Y until the typed
      // block-backed answer is reachable from here.
      //
      // Why not the insertion point ALONE: a drawing whose only entity is a
      // table would then yield a DEGENERATE extent (minX === maxX), and
      // `createViewportEntity` treats `modelW > 0 && modelH > 0` as the gate
      // for fitting at all — so a point-only answer reproduces the exact
      // (0, 0) / viewHeight-100 miss this arm exists to close.
      const position = parsePoint(props, "Position X", "Position Y");
      if (!position) return points;
      points.push(position);
      const width = parseNumericProperty(props, "Table width");
      const height = parseNumericProperty(props, "Table height");
      if (width === null || height === null) return points;
      const rotationRad =
        ((parseNumericProperty(props, "Rotation") ?? 0) * Math.PI) / 180;
      const cos = Math.cos(rotationRad);
      const sin = Math.sin(rotationRad);
      // `right` = the group-11 horizontal direction scaled by the table width;
      // `down` = that direction turned 90° clockwise, scaled by the height.
      const rightX = cos * width;
      const rightY = sin * width;
      const downX = sin * height;
      const downY = -cos * height;
      points.push({ x: position.x + rightX, y: position.y + rightY });
      points.push({ x: position.x + downX, y: position.y + downY });
      points.push({
        x: position.x + rightX + downX,
        y: position.y + rightY + downY,
      });
      return points;
    }

    case "OLE Frame": {
      // OLE2FRAME's two stored corners (groups 10/20 and 11/21) ARE the
      // entity's whole extent in WCS — AutoCAD clips the embedded picture to
      // exactly this frame, and `wcsBoundsLeaf.ts::oleFrameLocal` bounds the
      // typed entity from the very same pair with no extrusion. So unlike
      // every other arm here this one is EXACT, not an approximation.
      const upperLeft = parsePoint(props, "Upper left X", "Upper left Y");
      const lowerRight = parsePoint(props, "Lower right X", "Lower right Y");
      if (upperLeft) points.push(upperLeft);
      if (lowerRight) points.push(lowerRight);
      return points;
    }

    // Hatch, Viewport, Ellipse, Spline, Point, Wipeout: shell
    // EntityPreview does not carry positional coordinates we can read
    // here (boundary paths, paper-space entities, and rows this module
    // has no arm for yet). Return empty so they contribute nothing to
    // the extent.
    default:
      return points;
  }
}

/**
 * Compute the axis-aligned bounding box of the given shell entities.
 * Returns null when the input is empty or no entity contributes any
 * point (e.g. a model that contains only hatches, or only polylines
 * whose serialized vertex row is empty / malformed).
 */
export function computeModelExtent(
  entities: EntityPreview[],
): ModelExtent | null {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let haveAny = false;

  for (const entity of entities) {
    for (const point of extractEntityPoints(entity)) {
      // DOCUMENTED EQUIVALENT MUTANTS (targeted mutation testing), the four
      // `EqualityOperator` swaps on this fold: `<` → `<=` and `>` → `>=`. At
      // equality the assignment writes a value NUMERICALLY equal to the one
      // the accumulator already holds, so both forms compute the identical
      // box — the standard min/max-idiom equivalence, the same one
      // `geometryTransforms.ts`' `OLE Frame` corner fold records for its own
      // copy. The one non-BIT-identical case is SIGNED ZERO (`-0 < +0` is
      // false, `-0 <= +0` is true), which lets the mutant store `-0` where the
      // guarded form keeps `+0`; unobservable, because the two are `===`
      // everywhere downstream and the only consumer is `createViewportEntity`'s
      // arithmetic, where `-0` and `+0` agree in every operation it performs.
      if (point.x < minX) minX = point.x;
      if (point.y < minY) minY = point.y;
      if (point.x > maxX) maxX = point.x;
      if (point.y > maxY) maxY = point.y;
      haveAny = true;
    }
  }

  if (!haveAny) return null;
  return { minX, minY, maxX, maxY };
}
